import { Box, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { type ReactNode, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CODE_MASTER_TYPE } from '@/constants/code-master-type';
import {
  generateStationKey,
  type Reservation,
  type ReservationState,
  type StationState,
  type TimeInformation,
} from '@/features/driver/types/driveDetail';
import { formatMoney } from '@/features/driver/utils/processData';
import { useCodeMaster } from '@/modules/hooks/useCodeMaster';

type ReservationInfoComponentProps = {
  data: TimeInformation;
  active: boolean;
  isLastest: boolean;
  isBefore: boolean;
  onPickUp: (record: Reservation) => void;
  onCancel: (record: Reservation) => void;
  onDropoff: (record: Reservation) => void;
  stationArrivalState: StationState;
  reservationCompletionState: ReservationState;
  hideActionCards?: boolean;
  headerRight?: ReactNode;
};

export const ReservationInfoComponent: React.FC<ReservationInfoComponentProps> = ({
  data,
  active,
  isLastest,
  isBefore: _isBefore,
  onPickUp,
  onCancel,
  onDropoff,
  stationArrivalState,
  reservationCompletionState,
  hideActionCards = false,
  headerRight,
}) => {
  const { t } = useTranslation();
  const paymentMethodInVehicles = useCodeMaster(CODE_MASTER_TYPE.PAYMENT_METHOD_AT_RESERVATION);

  const getTotalPassengerCount = (reservation: Reservation): number =>
    reservation.passengerTypeList.reduce((total, passenger) => total + passenger.passengerCount, 0);

  const getActionDoneClass = (reservationId: number, mode: 'pickup' | 'dropoff'): string => {
    const state = reservationCompletionState[reservationId];
    if (!state) return '';

    const isDone =
      mode === 'pickup' ? state.pickUpClicked || state.cancelClicked : state.dropOffClicked || state.cancelClicked;

    return isDone ? ' station-card-action-done' : '';
  };

  const isActionDisabled = (reservationId: number, mode: 'pickup' | 'dropoff'): boolean => {
    const state = reservationCompletionState[reservationId];
    if (!state) return false;

    return mode === 'pickup' ? state.pickUpClicked || state.cancelClicked : state.dropOffClicked || state.cancelClicked;
  };

  const getPaymentMethodName = (paymentMethod: string) => {
    const paymentMethodInVehicle = paymentMethodInVehicles.find((item) => item.key === paymentMethod);

    return paymentMethodInVehicle?.value;
  };

  const renderReservationDetail = (reservation: Reservation) => {
    const passengerCountLabel = reservation.passengerTypeList
      .map((passenger) => `${passenger.passengerTypeName} ${passenger.passengerCount}${t('driveDetail.people')}`)
      .join(' ');

    return (
      <div className="detail-view-list">
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.reservationId').replace('：', '')}</span>
          <span className="detail-view-value">{reservation.reservationId}</span>
        </div>
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.passenger').replace('：', '')}</span>
          <span className="detail-view-value">{passengerCountLabel}</span>
        </div>
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.name').replace('：', '')}</span>
          <span className="detail-view-value">{`${reservation.userName}${t('driveDetail.mr')}`}</span>
        </div>
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.pickupTime').replace('：', '')}</span>
          <span className="detail-view-value">{dayjs(reservation.latestEstimatedPickupDatetime).format('HH:mm')}</span>
        </div>
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.pickupWaypoint').replace('：', '')}</span>
          <span className="detail-view-value">{reservation.pickupWaypointName}</span>
        </div>
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.dropoffTime').replace('：', '')}</span>
          <span className="detail-view-value">{dayjs(reservation.latestEstimatedDropoffDatetime).format('HH:mm')}</span>
        </div>
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.dropoffWaypoint').replace('：', '')}</span>
          <span className="detail-view-value">{reservation.dropoffWaypointName}</span>
        </div>
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.paymentMethod').replace('：', '')}</span>
          <span className="detail-view-value">{getPaymentMethodName(String(reservation.paymentMethod))}</span>
        </div>
        <div className="detail-view-row">
          <span className="detail-view-label">{t('driveDetail.price').replace('：', '')}</span>
          <span className="detail-view-value">{`${formatMoney(reservation.price)}${t('driveDetail.yen')}`}</span>
        </div>
        <div className="detail-view-message">
          <span className="detail-view-label">{t('driveDetail.message').replace('：', '')}</span>
          <span className="detail-view-message-text">{reservation.message}</span>
        </div>
      </div>
    );
  };

  const [expandedReservationIds, setExpandedReservationIds] = useState<Record<number, boolean>>({});

  const toggleDetail = useCallback((reservationId: number) => {
    setExpandedReservationIds((prev) => ({
      ...prev,
      [reservationId]: !prev[reservationId],
    }));
  }, []);

  const boxProps = active ? { py: 1 } : { py: 1, opacity: 0.8, color: 'gray' };
  const uniqueKey = generateStationKey(data.waypointId || 0, data.time || '', data.waypointSequence || '');
  const stationHasArrived = stationArrivalState[uniqueKey]?.hasArrived ?? false;
  const hasPickup = data.pickup.length > 0;
  const hasDropoff = data.dropoff.length > 0;
  const shouldRenderUnifiedHeader = hasPickup || hasDropoff;
  const shouldRenderHeaderRight = Boolean(headerRight) && shouldRenderUnifiedHeader;

  const waypointNumber = data.pickup[0]?.pickupWaypointNumber || data.dropoff[0]?.dropoffWaypointNumber || '';
  const waypointDisplayName = data.waypointName.replace(/^\S+\s+/, '');

  return (
    <Box key={data.key}>
      <Box sx={{ ...boxProps }}>
        {/* 同一時刻・同一乗降場所の共通ヘッダー（時刻・場所・GoogleMapボタン） */}
        {shouldRenderUnifiedHeader && (
          <div className={`station-header-unified${!active ? ' station-card-inactive' : ''}`}>
            <div className="station-card-top">
              {/* 1行目: 時刻・地点コード・地点名 */}
              <div className="station-card-info-row">
                <div className="station-card-header">
                  <span className="station-card-time">{dayjs(data.time).format('HH:mm')}</span>
                  {waypointNumber && <span className="station-waypoint-code">{waypointNumber}</span>}
                </div>
                <p className="station-waypoint-name">{waypointDisplayName}</p>
              </div>
              {/* 2行目: Mapで見る・到着ボタン */}
              {shouldRenderHeaderRight && <div className="station-card-header-right">{headerRight}</div>}
            </div>
          </div>
        )}

        {/* 乗車カードの表示 */}
        {hasPickup && (
          <div className={`station-card station-card-pickup${!active ? ' station-card-inactive' : ''}`}>
            <div className="station-card-content">
              {active && !hideActionCards && !isLastest && stationHasArrived
                ? data.pickup.map((pickupInfo) => {
                    const totalPassengerCount = getTotalPassengerCount(pickupInfo);

                    return (
                      <div
                        key={pickupInfo.reservationId}
                        className={`station-card-action-area${getActionDoneClass(pickupInfo.reservationId, 'pickup')}`}
                      >
                        <div className="station-card-action-area-content">
                          <div className="station-card-action-row">
                            <div className="station-card-action-badge" aria-hidden>
                              {t('driveDetail.embark')}
                            </div>
                            <div className="station-card-passenger-info">
                              <span className="station-card-passenger-name">
                                {`${pickupInfo.userName.split(' ')[0]}${t('driveDetail.mr')} ${t('driveDetail.parenthesisLeft')}${totalPassengerCount}${t('driveDetail.people')}${t('driveDetail.parenthesisRight')}`}
                              </span>
                              <span className="station-card-passenger-detail">
                                <span>
                                  {formatMoney(pickupInfo.price)}
                                  <span className="station-card-passenger-detail-yen">{t('driveDetail.yen')}</span>
                                </span>
                                <span>{t('driveDetail.pickup')}</span>
                              </span>
                            </div>
                            <Button
                              size="medium"
                              variant="outlined"
                              color="inherit"
                              className="station-btn-detail"
                              onClick={() => {
                                toggleDetail(pickupInfo.reservationId);
                              }}
                            >
                              {expandedReservationIds[pickupInfo.reservationId]
                                ? t('driveDetail.closeDetail')
                                : t('driveDetail.detail')}
                            </Button>
                          </div>
                          <div className="station-card-action-buttons">
                            <Button
                              size="medium"
                              variant="outlined"
                              color="inherit"
                              className="station-btn-cancel"
                              disabled={isActionDisabled(pickupInfo.reservationId, 'pickup')}
                              onClick={() => {
                                onCancel(pickupInfo);
                              }}
                            >
                              {t('driveDetail.cancel')}
                            </Button>
                            <Button
                              size="medium"
                              variant="outlined"
                              color="inherit"
                              className={
                                isActionDisabled(pickupInfo.reservationId, 'pickup')
                                  ? 'station-btn-disabled'
                                  : 'station-btn-pickup'
                              }
                              disabled={isActionDisabled(pickupInfo.reservationId, 'pickup')}
                              onClick={() => {
                                onPickUp(pickupInfo);
                              }}
                            >
                              {reservationCompletionState[pickupInfo.reservationId]?.pickUpClicked
                                ? t('driveDetail.pickupDone')
                                : t('driveDetail.pickup')}
                            </Button>
                          </div>
                        </div>
                        {expandedReservationIds[pickupInfo.reservationId] && (
                          <Box mx={2}>{renderReservationDetail(pickupInfo)}</Box>
                        )}
                      </div>
                    );
                  })
                : data.pickup.map((pickupInfo) => {
                    const totalPassengerCount = getTotalPassengerCount(pickupInfo);

                    return (
                      <div
                        key={pickupInfo.reservationId}
                        className="station-card-action-area station-card-action-area--no-actions"
                      >
                        <div className="station-card-action-area-content">
                          <div className="station-card-action-row">
                            <div className="station-card-action-area__faded">
                              <div className="station-card-action-badge" aria-hidden>
                                {t('driveDetail.embark')}
                              </div>
                              <div className="station-card-passenger-info">
                                <span className="station-card-passenger-name">
                                  {`${pickupInfo.userName.split(' ')[0]}${t('driveDetail.mr')} ${t('driveDetail.parenthesisLeft')}${totalPassengerCount}${t('driveDetail.people')}${t('driveDetail.parenthesisRight')}`}
                                </span>
                                <span className="station-card-passenger-detail">
                                  <span>
                                    {formatMoney(pickupInfo.price)}
                                    <span className="station-card-passenger-detail-yen">{t('driveDetail.yen')}</span>
                                  </span>
                                  <span>{t('driveDetail.pickup')}</span>
                                </span>
                              </div>
                            </div>
                            <Button
                              size="medium"
                              variant="outlined"
                              color="inherit"
                              className="station-btn-detail"
                              onClick={() => {
                                toggleDetail(pickupInfo.reservationId);
                              }}
                            >
                              {expandedReservationIds[pickupInfo.reservationId]
                                ? t('driveDetail.closeDetail')
                                : t('driveDetail.detail')}
                            </Button>
                          </div>
                        </div>
                        {expandedReservationIds[pickupInfo.reservationId] && (
                          <Box mx={2}>{renderReservationDetail(pickupInfo)}</Box>
                        )}
                      </div>
                    );
                  })}
            </div>
          </div>
        )}

        {/* 降車カードの表示 */}
        {hasDropoff && (
          <div className={`station-card station-card-dropoff${!active ? ' station-card-inactive' : ''}`}>
            <div className="station-card-content">
              {active
                ? data.dropoff.map((dropoffInfo) => {
                    const totalPassengerCount = getTotalPassengerCount(dropoffInfo);

                    return (
                      <div
                        key={dropoffInfo.reservationId}
                        className={`station-card-action-area${getActionDoneClass(dropoffInfo.reservationId, 'dropoff')}`}
                      >
                        <div className="station-card-action-area-content">
                          <div className="station-card-action-row">
                            <div className="station-card-action-badge" aria-hidden>
                              {t('driveDetail.disembark')}
                            </div>
                            <div className="station-card-passenger-info">
                              <span className="station-card-passenger-name">
                                {`${dropoffInfo.userName.split(' ')[0]}${t('driveDetail.mr')} ${t('driveDetail.parenthesisLeft')}${totalPassengerCount}${t('driveDetail.people')}${t('driveDetail.parenthesisRight')}`}
                              </span>
                              <span className="station-card-passenger-detail">
                                <span>
                                  {formatMoney(dropoffInfo.price)}
                                  <span className="station-card-passenger-detail-yen">{t('driveDetail.yen')}</span>
                                </span>
                                <span>{t('driveDetail.dropoff')}</span>
                              </span>
                            </div>
                            <Button
                              size="medium"
                              variant="outlined"
                              color="inherit"
                              className="station-btn-detail"
                              onClick={() => {
                                toggleDetail(dropoffInfo.reservationId);
                              }}
                            >
                              {expandedReservationIds[dropoffInfo.reservationId]
                                ? t('driveDetail.closeDetail')
                                : t('driveDetail.detail')}
                            </Button>
                          </div>
                          <div className="station-card-action-buttons station-card-action-buttons--dropoff-only">
                            <Button
                              size="medium"
                              variant="outlined"
                              color="inherit"
                              className={
                                isActionDisabled(dropoffInfo.reservationId, 'dropoff')
                                  ? 'station-btn-disabled'
                                  : 'station-btn-dropoff'
                              }
                              disabled={isActionDisabled(dropoffInfo.reservationId, 'dropoff')}
                              onClick={() => {
                                onDropoff(dropoffInfo);
                              }}
                            >
                              {reservationCompletionState[dropoffInfo.reservationId]?.dropOffClicked
                                ? t('driveDetail.dropoffDone')
                                : t('driveDetail.dropoff')}
                            </Button>
                          </div>
                        </div>
                        {expandedReservationIds[dropoffInfo.reservationId] && (
                          <Box mx={2}>{renderReservationDetail(dropoffInfo)}</Box>
                        )}
                      </div>
                    );
                  })
                : data.dropoff.map((dropoffInfo) => {
                    const totalPassengerCount = getTotalPassengerCount(dropoffInfo);

                    return (
                      <div
                        key={dropoffInfo.reservationId}
                        className="station-card-action-area station-card-action-area--no-actions"
                      >
                        <div className="station-card-action-area-content">
                          <div className="station-card-action-row">
                            <div className="station-card-action-area__faded">
                              <div className="station-card-action-badge" aria-hidden>
                                {t('driveDetail.disembark')}
                              </div>
                              <div className="station-card-passenger-info">
                                <span className="station-card-passenger-name">
                                  {`${dropoffInfo.userName.split(' ')[0]}${t('driveDetail.mr')} ${t('driveDetail.parenthesisLeft')}${totalPassengerCount}${t('driveDetail.people')}${t('driveDetail.parenthesisRight')}`}
                                </span>
                                <span className="station-card-passenger-detail">
                                  <span>
                                    {formatMoney(dropoffInfo.price)}
                                    <span className="station-card-passenger-detail-yen">{t('driveDetail.yen')}</span>
                                  </span>
                                  <span>{t('driveDetail.dropoff')}</span>
                                </span>
                              </div>
                            </div>
                            <Button
                              size="medium"
                              variant="outlined"
                              color="inherit"
                              className="station-btn-detail"
                              onClick={() => {
                                toggleDetail(dropoffInfo.reservationId);
                              }}
                            >
                              {expandedReservationIds[dropoffInfo.reservationId]
                                ? t('driveDetail.closeDetail')
                                : t('driveDetail.detail')}
                            </Button>
                          </div>
                        </div>
                        {expandedReservationIds[dropoffInfo.reservationId] && (
                          <Box mx={2}>{renderReservationDetail(dropoffInfo)}</Box>
                        )}
                      </div>
                    );
                  })}
            </div>
          </div>
        )}

        {!isLastest && (
          <Box display="flex" justifyContent="flex-end" pr={5}>
            <Typography
              textAlign="center"
              sx={{ color: 'gray' }}
            >{` ${t('driveDetail.timeRequired')}${data.timeDiff} ${t('driveDetail.minutes')}`}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
